import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import api from "../api";
import { philippinesToday } from "../utils/publicApartments";
import bgVideo from "../assets/lbg.mp4";
import {
  LEASE_DURATIONS,
  money,
  parseApartmentUnits,
} from "../utils/rentalPortal";

const readServerText = (value) => {
  if (!value) return "";
  if (typeof value === "string") return value;
  if (typeof value?.message === "string") return value.message;
  return "";
};

const toFriendlySubmitError = (error) => {
  const status = error?.response?.status;
  const serverText = readServerText(error?.response?.data).toLowerCase();
  if (serverText.includes("method not supported")) {
    return "Rental application POST route is not enabled on backend.";
  }
  if (status === 401 || status === 403) {
    return "Your session is not authorized to submit this rental application.";
  }
  if (status === 422) {
    return Object.values(error?.response?.data?.errors || {}).flat().join(" ") || error?.response?.data?.message || "Please complete all required application fields.";
  }
  if (status === 500) {
    return "Backend server error while saving rental application.";
  }
  return error?.response?.data?.message || "Unable to save rental application to database.";
};

export default function TenantRentalApplication() {
  const { unitId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [statusMessage, setStatusMessage] = useState("");
  const [units, setUnits] = useState([]);
  const [loadError, setLoadError] = useState("");

  const [form, setForm] = useState({
    selected_unit: unitId || "",
    full_name: localStorage.getItem("username") || "",
    email: localStorage.getItem("email") || "",
    phone: "",
    current_address: "",
    preferred_move_in_date: "",
    preferred_viewing_date: "",
    lease_duration: "",
    occupation_income: "",
    number_of_occupants: "",
    message_to_landlord: "",
  });

  useEffect(() => {
    const loadUnits = async () => {
      setLoading(true);
      try {
        const response = await api.get("/public/vacant-properties");
        setUnits(parseApartmentUnits(response.data));
      } catch (error) {
        console.error(error);
        setUnits([]);
        setLoadError("We could not load available apartments. Please go back and try again.");
      } finally {
        setLoading(false);
      }
    };
    loadUnits();
  }, []);

  const selectedUnit = useMemo(
    () => units.find((unit) => String(unit.id) === String(form.selected_unit)),
    [form.selected_unit, units]
  );
  const fieldClassName =
    "mt-2 w-full rounded-lg border border-white/10 bg-white px-3 py-2.5 text-slate-900 outline-none focus:border-teal-400 focus:ring-2 focus:ring-teal-400/30";
  const isSuccessMessage = statusMessage.startsWith("Rental application submitted.");

  const submitApplication = async (event) => {
    event.preventDefault();
    if (submitting || !selectedUnit) return;
    setSubmitting(true);
    setStatusMessage("");

    const payload = {
      unit_id: form.selected_unit,
      apartment_name: selectedUnit?.apartment_name || "",
      monthly_rent: selectedUnit?.monthly_rent || 0,
      full_name: form.full_name.trim(),
      email: form.email.trim(),
      phone: form.phone.trim(),
      current_address: form.current_address.trim(),
      preferred_move_in_date: form.preferred_move_in_date || null,
      preferred_viewing_date: form.preferred_viewing_date || null,
      lease_duration: form.lease_duration || null,
      occupation_income: form.occupation_income.trim(),
      number_of_occupants: Number(form.number_of_occupants || 0),
      message_to_landlord: form.message_to_landlord.trim(),
      application_status: "Pending",
      submitted_by: localStorage.getItem("email") || form.email.trim(),
    };

    try {
      await api.post("/tenant/rental-applications", payload);

      setStatusMessage("Rental application submitted. Application status is now Pending.");
      navigate("/tenant/applications");
    } catch (error) {
      console.error(error);
      setStatusMessage(toFriendlySubmitError(error));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="relative min-h-screen overflow-hidden bg-slate-950 font-sans text-white">
      <video data-workspace-background="true" aria-hidden="true" className="absolute inset-0 h-full w-full object-cover" src={bgVideo} autoPlay muted loop playsInline />
      <div className="absolute inset-0 bg-slate-950/92" />
      <div className="absolute inset-0 bg-gradient-to-br from-slate-950 via-slate-950/95 to-teal-950/80" />

      <div className="relative z-10 mx-auto w-full max-w-3xl px-4 py-6 sm:px-6 lg:px-8">
        <header className="mb-6 rounded-xl border border-white/10 bg-slate-950/92 p-6 shadow-2xl">
          <p className="text-xs font-bold uppercase tracking-[0.3em] text-teal-300">Tenant Application</p>
          <h1 className="mt-2 text-3xl font-bold text-white">Rental Application Form</h1>
          <p className="mt-2 text-sm text-slate-300">
            Send your details, then track your application. The landlord will contact you about a viewing and request documents when needed.
          </p>
        </header>

        {loading ? (
          <div className="rounded-xl border border-white/10 bg-slate-950/92 py-10 text-center text-slate-300 shadow-2xl">
            Loading apartment units...
          </div>
        ) : loadError || !selectedUnit ? (
          <div role="alert" className="rounded-xl border border-white/10 p-6"><p>{loadError || "This apartment is no longer available. Please choose another unit."}</p><Link to="/tenant/apartments" className="ws-text-link">Back to apartments</Link></div>
        ) : (
          <form onSubmit={submitApplication} className="rounded-xl border border-white/10 bg-slate-950/92 p-6 shadow-2xl">
            <div className="grid gap-4 md:grid-cols-2">
              <div className="md:col-span-2 rounded-lg bg-white/5 p-4"><h2 className="text-xl font-bold">{selectedUnit.apartment_name}</h2><p>{selectedUnit.location} · {money(selectedUnit.monthly_rent)} / month</p><Link to="/tenant/apartments" className="ws-text-link">Choose another apartment</Link></div>
              <div className="md:col-span-2">
                <label className="text-sm font-semibold text-slate-200">Preferred viewing date
                  <input type="date" required min={philippinesToday()} value={form.preferred_viewing_date} onChange={(event) => setForm((prev) => ({ ...prev, preferred_viewing_date: event.target.value }))} className={fieldClassName} aria-describedby="viewing-date-help" />
                </label>
                <p id="viewing-date-help" className="mt-2 text-xs text-slate-400">Choose a day you would like to visit. The landlord will confirm availability; this is a request, not a confirmed appointment.</p>
              </div>

              <label className="text-sm font-semibold text-slate-200">
                Full Name
                <input
                  required
                  value={form.full_name}
                  onChange={(event) => setForm((prev) => ({ ...prev, full_name: event.target.value }))}
                  className={fieldClassName}
                />
              </label>

              <label className="text-sm font-semibold text-slate-200">
                Email
                <input
                  type="email"
                  required
                  value={form.email}
                  onChange={(event) => setForm((prev) => ({ ...prev, email: event.target.value }))}
                  className={fieldClassName}
                />
              </label>

              <label className="text-sm font-semibold text-slate-200">
                Phone Number
                <input
                  required
                  value={form.phone}
                  onChange={(event) => setForm((prev) => ({ ...prev, phone: event.target.value }))}
                  className={fieldClassName}
                />
              </label>

              <label className="md:col-span-2 text-sm font-semibold text-slate-200">
                Current Address
                <input
                  required
                  value={form.current_address}
                  onChange={(event) => setForm((prev) => ({ ...prev, current_address: event.target.value }))}
                  className={fieldClassName}
                />
              </label>

              <label className="text-sm font-semibold text-slate-200">
                Occupation / Source of Income
                <input
                  required
                  value={form.occupation_income}
                  onChange={(event) => setForm((prev) => ({ ...prev, occupation_income: event.target.value }))}
                  className={fieldClassName}
                />
              </label>

              <label className="text-sm font-semibold text-slate-200">
                Number of Occupants
                <input
                  type="number"
                  min="1"
                  required
                  value={form.number_of_occupants}
                  onChange={(event) =>
                    setForm((prev) => ({ ...prev, number_of_occupants: event.target.value }))
                  }
                  className={fieldClassName}
                />
              </label>

              <details className="md:col-span-2"><summary className="cursor-pointer text-sm font-semibold">Optional: move-in plans and a message</summary><div className="mt-4 grid gap-4 md:grid-cols-2">
              <label className="text-sm font-semibold text-slate-200">
                Preferred Move-in Date
                <input
                  type="date"
                  value={form.preferred_move_in_date}
                  onChange={(event) =>
                    setForm((prev) => ({ ...prev, preferred_move_in_date: event.target.value }))
                  }
                  className={fieldClassName}
                />
              </label>

              <label className="text-sm font-semibold text-slate-200">
                Lease Duration
                <select
                  value={form.lease_duration}
                  onChange={(event) => setForm((prev) => ({ ...prev, lease_duration: event.target.value }))}
                  className={fieldClassName}
                >
                  <option value="">Select duration</option>
                  {LEASE_DURATIONS.map((duration) => (
                    <option key={duration} value={duration}>
                      {duration}
                    </option>
                  ))}
                </select>
              </label>

              <label className="md:col-span-2 text-sm font-semibold text-slate-200">
                Message to Landlord/Admin
                <textarea
                  rows={4}
                  value={form.message_to_landlord}
                  onChange={(event) =>
                    setForm((prev) => ({ ...prev, message_to_landlord: event.target.value }))
                  }
                  className={fieldClassName}
                />
              </label>

              </div></details>
            </div>

            <div className="mt-5 flex flex-wrap gap-2">
              <button
                type="submit"
                disabled={submitting || !selectedUnit}
                className="rounded-lg bg-teal-600 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-teal-500 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {submitting ? "Submitting..." : "Submit Rental Application"}
              </button>
              <Link
                to="/tenant/applications"
                className="rounded-lg border border-white/15 px-4 py-2.5 text-sm font-semibold text-slate-100 transition hover:bg-white/10"
              >
                My applications
              </Link>
            </div>

            {statusMessage ? (
              <p
                role="alert"
                className={`mt-3 rounded-lg border px-3 py-2 text-sm ${
                  isSuccessMessage
                    ? "border-emerald-300/30 bg-emerald-500/10 text-emerald-100"
                    : "border-red-300/30 bg-red-500/10 text-red-100"
                }`}
              >
                {statusMessage}
              </p>
            ) : null}
          </form>
        )}
      </div>
    </div>
  );
}
